import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import graspdLogo from '../assets/graspd-logo.svg';
import '../styles/splash.css';
import '../styles/legal.css';

const PrivacyPage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleBack = () => {
        // Go back if there's history, otherwise go to home
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="splash-page-root legal-page-root">
            <SEO
                title="Privacy Policy"
                description="Learn how Graspd collects, uses, and protects your information when you use our AI-powered learning platform."
                path="/privacy"
            />

            {/* Minimal Header */}
            <header className="legal-minimal-header">
                <button
                    onClick={handleBack}
                    className="legal-back-btn"
                    aria-label="Go back"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" />
                        <path d="M12 19l-7-7 7-7" />
                    </svg>
                    <span>Back</span>
                </button>
                <Link to="/" className="legal-logo-link" aria-label="Go to homepage">
                    <img src={graspdLogo} alt="Graspd" className="legal-logo" />
                </Link>
                <div className="legal-header-spacer" />
            </header>

            <main className="legal-page">
                <div className="legal-container">
                    <header className="legal-header">
                        <h1>Privacy Policy</h1>
                        <p className="legal-updated">Last updated: January 2025</p>
                    </header>

                    <div className="legal-content">
                        <section>
                            <h2>1. Introduction</h2>
                            <p>
                                Graspd, LLC ("Graspd," "we," "our," or "us") is an Arizona limited liability company
                                committed to transparency about how we collect, use, own, and manage your information
                                when you use our AI-powered learning platform and services. By using our Service, you
                                acknowledge and consent to the data practices described in this Privacy Policy.
                            </p>
                        </section>

                        <section>
                            <h2>2. Information We Collect</h2>
                            <p>
                                <strong>Account Information:</strong> When you create an account, we collect your name,
                                email address, and password.
                            </p>
                            <p>
                                <strong>Payment Information:</strong> When you subscribe, payment is processed securely
                                through Stripe. We do not store your full credit card information on our servers.
                            </p>
                            <p>
                                <strong>Learning Profile Data:</strong> We collect comprehensive information about your
                                learning activities, including but not limited to: courses created and accessed, learning
                                progress and completion rates, quiz and assessment responses, knowledge check results,
                                time spent on lessons and sections, learning preferences and patterns, topics of interest,
                                skill levels and mastery progression, interaction patterns with AI-generated content,
                                questions asked through our "Ask Graspd" feature, and any other data generated through
                                your use of the platform.
                            </p>
                            <p>
                                <strong>User-Generated Content:</strong> We collect all content you create or input into
                                the platform, including course requests, content extracted from uploaded documents (such
                                as text and information parsed from PDFs), notes, and any other materials you provide.
                                Uploaded files are processed to extract relevant content and are not stored in their
                                original form.
                            </p>
                            <p>
                                <strong>Device and Technical Information:</strong> We collect information about your
                                device, browser type, IP address, operating system, and usage patterns for analytics,
                                security, and service improvement purposes.
                            </p>
                        </section>

                        <section>
                            <h2>3. Data Ownership</h2>
                            <p>
                                <strong>Graspd's Ownership Rights:</strong> By using our Service, you acknowledge and
                                agree that all data collected through your use of the platform, including but not limited
                                to your Learning Profile Data, usage patterns, assessment results, and aggregated insights
                                derived therefrom, are owned by Graspd. This data constitutes valuable proprietary assets
                                of Graspd.
                            </p>
                            <p>
                                <strong>License Grant:</strong> To the extent that any data you provide may be subject to
                                intellectual property rights, you hereby grant Graspd a perpetual, irrevocable, worldwide,
                                royalty-free, fully sublicensable license to use, reproduce, modify, adapt, publish,
                                translate, create derivative works from, distribute, perform, display, and otherwise
                                exploit such data in any form, media, or technology now known or later developed, for
                                any purpose whatsoever.
                            </p>
                        </section>

                        <section>
                            <h2>4. How We Use Your Information</h2>
                            <p>Graspd may use your information for any lawful purpose, including but not limited to:</p>
                            <ul>
                                <li>Providing, personalizing, and improving our learning services</li>
                                <li>Processing transactions and managing subscriptions</li>
                                <li>Tracking learning progress and providing recommendations</li>
                                <li>Training, developing, and improving our AI and machine learning models</li>
                                <li>Conducting research and analytics on learning patterns and educational outcomes</li>
                                <li>Creating aggregated, anonymized, or de-identified data sets for any purpose</li>
                                <li>Developing new products, services, and features</li>
                                <li>Marketing and promotional purposes</li>
                                <li>Licensing or selling aggregated or anonymized data insights to third parties</li>
                                <li>Any other purpose that Graspd determines to be beneficial to its business operations</li>
                            </ul>
                        </section>

                        <section>
                            <h2>5. Information Sharing and Disclosure</h2>
                            <p>Graspd may share your information in the following circumstances:</p>
                            <ul>
                                <li>
                                    <strong>Service Providers:</strong> With third-party vendors who assist in operating
                                    our platform (such as Stripe for payments, cloud hosting providers, and analytics services)
                                </li>
                                <li>
                                    <strong>Aggregated Data:</strong> We may share, sell, or license aggregated, anonymized,
                                    or de-identified data that does not personally identify you to third parties for research,
                                    analytics, marketing, or other commercial purposes
                                </li>
                                <li>
                                    <strong>Business Transfers:</strong> In connection with any merger, acquisition, sale
                                    of assets, or other business transaction, your data may be transferred as a business asset
                                </li>
                                <li>
                                    <strong>Legal Requirements:</strong> When required by law, court order, or governmental authority
                                </li>
                                <li>
                                    <strong>Protection of Rights:</strong> To protect the rights, property, or safety of
                                    Graspd, our users, or others
                                </li>
                            </ul>
                        </section>

                        <section>
                            <h2>6. Data Retention</h2>
                            <p>
                                Graspd retains your data indefinitely, even after account termination, to the extent
                                permitted by law. This includes your Learning Profile Data and any derived insights or
                                analytics. We retain this data to improve our services, conduct research, and for other
                                lawful business purposes.
                            </p>
                        </section>

                        <section>
                            <h2>7. Data Security</h2>
                            <p>
                                We implement appropriate technical and organizational security measures to protect your
                                personal information. However, no method of transmission over the Internet is 100% secure,
                                and we cannot guarantee absolute security.
                            </p>
                        </section>

                        <section>
                            <h2>8. Your Rights</h2>
                            <p>Subject to applicable law and Graspd's data ownership rights, you may:</p>
                            <ul>
                                <li>Access certain personal data associated with your account</li>
                                <li>Correct inaccurate personal data</li>
                                <li>
                                    Request deletion of your account (note: Graspd may retain aggregated, anonymized,
                                    or de-identified data derived from your usage)
                                </li>
                                <li>Cancel your subscription at any time</li>
                            </ul>
                            <p>
                                To exercise these rights, contact us at{' '}
                                <a href="mailto:contact@graspd.ai">contact@graspd.ai</a>.
                            </p>
                        </section>

                        <section>
                            <h2>9. Children's Privacy</h2>
                            <p>
                                Our Service is not intended for children under 13 years of age. We do not knowingly
                                collect personal information from children under 13. If you are a parent or guardian
                                and believe your child has provided us with personal information, please contact us.
                            </p>
                        </section>

                        <section>
                            <h2>10. Cookies and Tracking</h2>
                            <p>
                                We use essential cookies to maintain your session and preferences. We also use analytics
                                and tracking technologies to understand how users interact with our platform, improve our
                                services, and for other purposes described in this policy.
                            </p>
                        </section>

                        <section>
                            <h2>11. Changes to This Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time. We will notify you of any material
                                changes by posting the new policy on this page and updating the "Last updated" date. Your
                                continued use of the Service after such changes constitutes acceptance of the updated policy.
                            </p>
                        </section>

                        <section>
                            <h2>12. Governing Law</h2>
                            <p>
                                This Privacy Policy shall be governed by and construed in accordance with the laws of the
                                State of Arizona and applicable federal laws of the United States, without regard to
                                conflict of law principles.
                            </p>
                        </section>

                        <section>
                            <h2>13. Contact Us</h2>
                            <p>
                                If you have any questions about this Privacy Policy, please contact us at{' '}
                                <a href="mailto:contact@graspd.ai">contact@graspd.ai</a>.
                            </p>
                        </section>

                        <div className="legal-cross-link">
                            <p>
                                Please also review our <Link to="/terms">Terms of Service</Link>.
                            </p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PrivacyPage;
